import { useIntl } from '@finn/ui-utils';
import { useCallback, useMemo } from 'react';

import { FilterKey, FiltersResponse } from '../../../core';
import { FiltersState, useFiltersResponse } from '../../../filters-management';
import { FilterData, FilterToggleData, TestProps } from '../../helpers';
import { FilterAccordion } from '../atoms/FilterAccordion';
import { FilterOptions } from '../atoms/FilterOptions';

type Props = TestProps & {
  selectedFilters: FiltersState;
  availableFilters: FiltersResponse;
  onOptionClick: (filterObject: FiltersState) => void;
};

export const FinnFilter = ({
  selectedFilters,
  availableFilters,
  onOptionClick,
  testId,
}: Props) => {
  const i18n = useIntl();
  const filtersResponse = useFiltersResponse();

  const selectedColors = useMemo(
    () => selectedFilters[FilterKey.COLORS as keyof FiltersState] || [],
    [selectedFilters]
  );

  const availableColors = useMemo(() => {
    const newColors = filtersResponse[FilterKey.COLORS]
      .map((configItem) => ({
        value: configItem.id,
        label: configItem.id,
        color: configItem.color_hex,
        selected: (selectedColors as string[])?.includes(configItem.id),
        disabled: false,
      }))
      .filter((item) => Boolean(item.color))
      .sort(
        (a, b) => a.color.localeCompare(b.color) * -1
      ) as FilterToggleData[];

    newColors.unshift({
      value: undefined,
      label: i18n.formatMessage('plp.all'),
      color: undefined,
      selected: !selectedColors,
      disabled: false,
    });

    return newColors;
  }, [
    filtersResponse[FilterKey.COLORS],
    availableFilters[FilterKey.COLORS],
    selectedColors,
  ]);

  const colorOptions = useMemo<FilterData>(
    () => ({
      filterKey: FilterKey.COLORS,
      selectedValues: selectedColors,
      availableFilterValues: availableColors,
    }),
    [filtersResponse[FilterKey.COLORS], availableColors, selectedColors]
  );

  const handleClick = useCallback(
    (value: object) => {
      onOptionClick(value);
    },
    [onOptionClick]
  );

  return (
    <FilterAccordion title={'Finn Only'} testId={testId}>
      <div className="mt-6 body-12-semibold">
        {i18n.formatMessage('plp.color')}
      </div>
      <FilterOptions data={colorOptions} onClick={handleClick} />
    </FilterAccordion>
  );
};
